const BASE = '/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  })
  const data = await res.json() as any
  if (!res.ok || !data.success) {
    throw new Error(data.error || `Request failed: ${res.status}`)
  }
  return data
}

export const api = {
  // Auth
  register: (email: string, password: string, displayName?: string) =>
    request('/auth/register', { method: 'POST', body: JSON.stringify({ email, password, displayName }) }),
  login: (email: string, password: string) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  getMe: () => request<any>('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    request('/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  updatePreferences: (prefs: { displayName?: string; preferredLang?: string }) =>
    request('/auth/preferences', { method: 'PATCH', body: JSON.stringify(prefs) }),

  // Catalog
  getCatalogTree: () => request<any>('/catalog/tree'),
  getQuestionCount: (paper: number, topic?: string, chapter?: string) => {
    const params = new URLSearchParams({ paper: String(paper) })
    if (topic) params.set('topic', topic)
    if (chapter) params.set('chapter', chapter)
    return request<any>(`/catalog/questions?${params}`)
  },

  // Quiz
  startQuiz: (opts: { mode: string; paper?: number; topic?: string; chapter?: string; questionCount?: number }) =>
    request<any>('/quiz/start', { method: 'POST', body: JSON.stringify(opts) }),
  getQuiz: (id: string) => request<any>(`/quiz/${id}`),
  submitAnswer: (attemptId: string, questionId: string, selectedOption: string, responseTimeMs?: number) =>
    request<any>(`/quiz/${attemptId}/answer`, {
      method: 'POST',
      body: JSON.stringify({ questionId, selectedOption, responseTimeMs }),
    }),
  submitQuiz: (id: string) => request<any>(`/quiz/${id}/submit`, { method: 'POST' }),
  abandonQuiz: (id: string) => request<any>(`/quiz/${id}/abandon`, { method: 'POST' }),

  // Me
  getDashboard: () => request<any>('/me/dashboard'),
  getHistory: (page = 1, pageSize = 20) => request<any>(`/me/history?page=${page}&pageSize=${pageSize}`),
  getWrongQuestions: (page = 1, paper?: number) => {
    const params = new URLSearchParams({ page: String(page) })
    if (paper) params.set('paper', String(paper))
    return request<any>(`/me/wrong-questions?${params}`)
  },
  getBookmarks: (page = 1) => request<any>(`/me/bookmarks?page=${page}`),
  addBookmark: (questionId: string, note?: string) =>
    request(`/me/bookmarks/${questionId}`, { method: 'POST', body: JSON.stringify({ note }) }),
  removeBookmark: (questionId: string) =>
    request(`/me/bookmarks/${questionId}`, { method: 'DELETE' }),
  getProgress: () => request<any>('/me/progress'),

  // Admin
  adminGetQuestions: (params: Record<string, string> = {}) =>
    request<any>(`/admin/questions?${new URLSearchParams(params)}`),
  adminGetQuestion: (id: string) => request<any>(`/admin/questions/${id}`),
  adminCreateQuestion: (data: any) =>
    request('/admin/questions', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdateQuestion: (id: string, data: any) =>
    request(`/admin/questions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminUpdateQuestionStatus: (id: string, status: string) =>
    request(`/admin/questions/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  adminGetUsers: (params: Record<string, string> = {}) =>
    request<any>(`/admin/users?${new URLSearchParams(params)}`),
  adminGetUser: (id: string) => request<any>(`/admin/users/${id}`),
  adminUpdateUser: (id: string, data: any) =>
    request(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  adminCreateUser: (data: any) =>
    request('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  adminGetStats: () => request<any>('/admin/stats'),
  adminImport: (questions: any[]) =>
    request('/admin/import', { method: 'POST', body: JSON.stringify({ questions }) }),
  adminExport: () => request<any>('/admin/export'),

  // Feedback
  submitFeedback: (data: {questionId: string; attemptId?: string; category: string; comment?: string}) =>
    request('/feedback', { method: 'POST', body: JSON.stringify(data) }),

  // Enhanced Me
  getDueReviews: () => request<any>('/me/due-reviews'),
  getReadiness: (paper: number) => request<any>(`/me/readiness/${paper}`),
  getTopicAnalytics: (paper: number) => request<any>(`/me/topic-analytics/${paper}`),

  // Enhanced Admin
  adminGetQuestionAnalytics: (params: Record<string, string> = {}) =>
    request<any>(`/admin/question-analytics?${new URLSearchParams(params)}`),
  adminGetFeedback: (params: Record<string, string> = {}) =>
    request<any>(`/admin/feedback?${new URLSearchParams(params)}`),
  adminGetFeedbackSummary: () => request<any>('/admin/feedback/summary'),
  adminResolveFeedback: (id: string, status: string, adminNote?: string) =>
    request(`/admin/feedback/${id}`, { method: 'PATCH', body: JSON.stringify({ status, adminNote }) }),
}
